// EmploymentList.tsx
// Lists employment entries, mirrors EducationList
import { DetailData, EmploymentResponse, SkillData, useApiClient } from '@/lib/api';
import { monthInputToApiDate } from '@/lib/profile-dates';
import React, { Dispatch, SetStateAction, useState } from 'react';
import EmploymentView from './EmploymentView';

interface EmploymentListProps {
  employments?: EmploymentResponse[];
  setEmployments?: Dispatch<SetStateAction<EmploymentResponse[]>>;
  details?: { [employmentId: string]: DetailData[] };
  setDetails?: Dispatch<SetStateAction<{ [employmentId: string]: DetailData[] }>>;
  skills?: { [employmentId: string]: SkillData[] };
  setSkills?: Dispatch<SetStateAction<{ [employmentId: string]: SkillData[] }>>;
  loading?: boolean;
  setLoading?: Dispatch<SetStateAction<boolean>>;
}

const EmploymentList: React.FC<EmploymentListProps> = ({
  employments,
  setEmployments,
  details,
  setDetails,
  skills,
  setSkills,
  loading,
  setLoading,
}) => {
  const [internalEmploymentList, setInternalEmploymentList] = useState<EmploymentResponse[]>([]);
  const [internalDetails, setInternalDetails] = useState<{ [key: string]: DetailData[] }>({});
  const [internalSkills, setInternalSkills] = useState<{ [key: string]: SkillData[] }>({});
  const [internalIsLoading, internalSetIsLoading] = useState(false);

  const employmentList = employments ?? internalEmploymentList;
  const employmentDetails = details ?? internalDetails;
  const employmentSkills = skills ?? internalSkills;

  const setEmploymentList = setEmployments ?? setInternalEmploymentList;
  const setEmploymentDetails = setDetails ?? setInternalDetails;
  const setEmploymentSkills = setSkills ?? setInternalSkills;

  const isLoading = loading ?? internalIsLoading;
  const setIsLoading = setLoading ?? internalSetIsLoading;

  const { api } = useApiClient();

  const onDeleteEmployment = async (id?: string) => {
    if (!id) return;
    setIsLoading(true);
    try {
      await api.deleteEmployment(id);
      setEmploymentList(list => list.filter(e => e.id !== id));
    } catch (error) {
      console.error('Error deleting employment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const onUpdateEmployment = async (employment: EmploymentResponse) => {
    if (!employment.id) return;
    setIsLoading(true);
    try {
      const updatedEmployment = await api.updateEmployment(employment.id, {
        title: employment.title,
        company: employment.company,
        start_date: monthInputToApiDate(employment.start_date) || null,
        end_date: employment.is_ongoing ? null : monthInputToApiDate(employment.end_date) || null,
        is_ongoing: Boolean(employment.is_ongoing),
      });
      setEmploymentList(list => list.map(item => item.id === employment.id ? updatedEmployment : item));
    } catch (error) {
      console.error('Error updating employment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!employmentList || employmentList.length === 0) {
    return <p className="text-sm text-gray-500 italic">No employment entries yet.</p>;
  }

  const sortedList = [...employmentList].sort((a, b) => {
    if (a.is_ongoing && !b.is_ongoing) return -1;
    if (!a.is_ongoing && b.is_ongoing) return 1;
    if (a.end_date && b.end_date) {
      return new Date(b.end_date).getTime() - new Date(a.end_date).getTime();
    }
    return new Date(b.start_date).getTime() - new Date(a.start_date).getTime();
  });

  return (
    <div className="space-y-4 sm:space-y-6">
      {sortedList.map((emp, idx) => (
        <EmploymentView
          key={emp.id || idx}
          employment={emp}
          details={employmentDetails[emp.id!] ?? []}
          skills={employmentSkills[emp.id!] ?? []}
          onDelete={onDeleteEmployment}
          onUpdate={onUpdateEmployment}
          onDetailsChange={(nextDetails) => {
            if (!emp.id) return;
            setEmploymentDetails(prev => ({ ...prev, [emp.id!]: nextDetails }));
          }}
          loading={isLoading}
        />
      ))}
    </div>
  );
};

export default EmploymentList;
